import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from './supabase'
import { saveReferenceCache, loadReferenceCache, clearReferenceCache } from './offlineStore'

const GALLERIES_CACHE_KEY = 'service_galleries'

const compressImage = (file, maxWidth = 1200, maxHeight = 800, quality = 0.85) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
            const img = new Image()
            img.onload = () => {
                const canvas = document.createElement('canvas')
                let width = img.width
                let height = img.height
                if (width > height) {
                    if (width > maxWidth) { height = Math.round((height * maxWidth) / width); width = maxWidth }
                } else {
                    if (height > maxHeight) { width = Math.round((width * maxHeight) / height); height = maxHeight }
                }
                canvas.width = width
                canvas.height = height
                const ctx = canvas.getContext('2d')
                ctx.drawImage(img, 0, 0, width, height)
                canvas.toBlob(
                    (blob) => blob ? resolve(new File([blob], file.name, { type: 'image/webp' })) : reject(new Error('Compression failed')),
                    'image/webp',
                    quality
                )
            }
            img.onerror = reject
            img.src = e.target.result
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
    })
}

export function useGallery(serviceKey) {
    const [images, setImages] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        if (!serviceKey) { setLoading(false); return }
        setLoading(true)
        supabase
            .from('gallery_images')
            .select('id, url, storage_path, label, sort_order, gallery_id, service_galleries!inner(service_key)')
            .eq('service_galleries.service_key', serviceKey)
            .order('sort_order', { ascending: true })
            .order('created_at', { ascending: true })
            .then(({ data, error: err }) => {
                if (err) setError(err)
                else setImages(data || [])
                setLoading(false)
            })
    }, [serviceKey])

    return { images, loading, error }
}

export function useGalleries() {
    const [galleries, setGalleries] = useState([])
    const [loading, setLoading] = useState(true)
    const [tick, setTick] = useState(0)
    const loadIdRef = useRef(0)

    const fetchAndApply = useCallback(async (id, silent) => {
        if (!silent) {
            setLoading(true)
        }

        const cached = await loadReferenceCache(GALLERIES_CACHE_KEY, null).catch(() => null)
        if (cached) {
            setGalleries(cached.data)
            if (!silent) setLoading(false)
        }

        const { data, error: err } = await supabase
            .from('service_galleries')
            .select('id, service_key, service_label, created_at')
            .order('created_at', { ascending: true })

        if (id !== loadIdRef.current) return

        if (err) {
            console.error('[useGalleries] fetch error:', err.message)
            if (!cached) setGalleries([])
        } else {
            setGalleries(data || [])
            await saveReferenceCache(GALLERIES_CACHE_KEY, data || [], null).catch(() => {})
        }
        if (!silent) setLoading(false)
    }, [])

    useEffect(() => {
        const id = ++loadIdRef.current
        fetchAndApply(id, false)
    }, [tick, fetchAndApply])

    const refresh = useCallback(() => setTick(t => t + 1), [])
    const silentReload = useCallback(async () => {
        const id = ++loadIdRef.current
        await fetchAndApply(id, true)
    }, [fetchAndApply])

    return { galleries, loading, refresh, silentReload }
}

export async function fetchGalleryImages(galleryId) {
    const { data, error } = await supabase
        .from('gallery_images')
        .select('id, url, storage_path, label, sort_order, created_at')
        .eq('gallery_id', galleryId)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true })
    if (error) throw error
    return data || []
}

export async function uploadGalleryImage(galleryId, serviceKey, file, label = '') {
    const compressed = await compressImage(file)
    const ext = 'webp'
    const filename = `gallery-${serviceKey}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${ext}`
    const storagePath = `gallery/${serviceKey}/${filename}`

    const { error: uploadErr } = await supabase.storage
        .from('store-images')
        .upload(storagePath, compressed, { cacheControl: '3600', upsert: false })
    if (uploadErr) throw uploadErr

    const { data: { publicUrl } } = supabase.storage.from('store-images').getPublicUrl(storagePath)

    const { data, error: insertErr } = await supabase
        .from('gallery_images')
        .insert({ gallery_id: galleryId, url: publicUrl, storage_path: storagePath, label: label || null, sort_order: 0 })
        .select()
        .single()
    if (insertErr) throw insertErr
    return data
}

export async function deleteGalleryImage(imageId, storagePath) {
    if (storagePath) {
        const { error: storageErr } = await supabase.storage.from('store-images').remove([storagePath])
        if (storageErr) throw storageErr
    }
    const { error } = await supabase.from('gallery_images').delete().eq('id', imageId)
    if (error) throw error
}

export async function deleteGallery(galleryId) {
    // Clean up storage files first
    const { data: images, error: fetchErr } = await supabase
        .from('gallery_images')
        .select('storage_path')
        .eq('gallery_id', galleryId)
    if (fetchErr) throw fetchErr
    const paths = (images || []).map(i => i.storage_path).filter(Boolean)
    if (paths.length > 0) {
        const { error: storageErr } = await supabase.storage.from('store-images').remove(paths)
        if (storageErr) throw storageErr
    }
    const { error } = await supabase.from('service_galleries').delete().eq('id', galleryId)
    if (error) throw error
    await clearReferenceCache(GALLERIES_CACHE_KEY, null).catch(() => {})
}

export async function replaceGalleryImage(imageId, serviceKey, file) {
    const compressed = await compressImage(file)
    const ext = 'webp'
    const filename = `gallery-${serviceKey}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${ext}`
    const storagePath = `gallery/${serviceKey}/${filename}`

    const { error: uploadErr } = await supabase.storage
        .from('store-images')
        .upload(storagePath, compressed, { cacheControl: '3600', upsert: false })
    if (uploadErr) throw uploadErr

    const { data: { publicUrl } } = supabase.storage.from('store-images').getPublicUrl(storagePath)

    // Fetch old storage_path to clean up later
    const { data: oldRow, error: oldErr } = await supabase
        .from('gallery_images')
        .select('storage_path')
        .eq('id', imageId)
        .single()
    if (oldErr) throw oldErr

    const { data, error: updateErr } = await supabase
        .from('gallery_images')
        .update({ url: publicUrl, storage_path: storagePath })
        .eq('id', imageId)
        .select()
        .single()
    if (updateErr) throw updateErr

    // Clean up old file
    if (oldRow?.storage_path) {
        await supabase.storage.from('store-images').remove([oldRow.storage_path])
    }
    return data
}

export async function updateGalleryLabel(imageId, label) {
    const { error } = await supabase
        .from('gallery_images')
        .update({ label: label || null })
        .eq('id', imageId)
    if (error) throw error
}

export async function createGallery(serviceKey, serviceLabel) {
    const { data, error } = await supabase
        .from('service_galleries')
        .insert({ service_key: serviceKey, service_label: serviceLabel })
        .select()
        .single()
    if (error) throw error
    await clearReferenceCache(GALLERIES_CACHE_KEY, null).catch(() => {})
    return data
}
